import * as React from 'react';
import { isModelWithError, ReportWidget } from '../../misc/api/apiTypes';
import { ReportWidgets } from './report-widgets';
import { DocumentsWidgetApp } from '../../DocumentsWidget/DocumentsWidgetApp';
import { HasPermission } from '../../misc/components/Authorization';
import { useSecurityContext } from '../../Core/SecurityContext';
import { useCurrentStudyConfigurationContext } from '../../Core/CurrentStudyConfigurationContext';
import { PermissionEnum } from '../../misc/api/ApiPermissionEnum';
import { ProgressWidgetApp } from '../../progress-widget/progress-widget-app';
import { CoughSessionBreakdownWidget } from '../../cough-session-breakdown-widget/components/cough-session-breakdown-widget';
import { SpirometrySessionsWidget } from '../../SpirometrySessionsWidget/SpirometrySessionsWidget';
import { SpirometrySessionBreakdownWidget } from '../../SpirometrySessionBreakdownWidget/SpirometrySessionBreakdownWidget';
import { useWebPortalLegacyClient } from '../../misc/api/WebPortalTokenClient';
import { WelcomeWidget } from './welcome-widget';
import { faCircleNotch, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const LoadingWidgets = () => {
    return (
        <div style={{ fontSize: '100px' }} className='text-center text-info'><FontAwesomeIcon icon={faCircleNotch} className="fa-spin" /></div>
    );
}

const NoWidgets = () => {
    return (
        <div className='alert alert-info'><FontAwesomeIcon icon={faInfoCircle} /> No dashboard widgets available.</div>
    );
}

export const Dashboard = ({ }) => {
    const { currentUserPermissions } = useSecurityContext();
    const { study } = useCurrentStudyConfigurationContext();
    const [reportWidgets, setReportWidgets] = React.useState<ReportWidget[]>();
    const [columns, setColumns] = React.useState(2);
    const legacyClient = useWebPortalLegacyClient();

    React.useEffect(() => {
        const init = async () => {
            const reportWidgetsResult = await legacyClient.getReportWidgets()
                .then(json => {
                    if (isModelWithError(json)) {
                        return [];
                    }
                    return json;
                });
            setReportWidgets(reportWidgetsResult);
        }

        init();
    }, []);

    if (!reportWidgets)
        return <LoadingWidgets />;

    const hasDocWidgetAccess = HasPermission(currentUserPermissions, study.id, PermissionEnum.ViewDocumentsWidget);
    const hasProgressWidgetAccess = HasPermission(currentUserPermissions, study.id, PermissionEnum.ViewStudyProgressWidget);
    const hasCoughSessionBreakdownAccess = HasPermission(currentUserPermissions, study.id, PermissionEnum.ViewCoughSessionBreakdownWidget);
    const hasSpirometrySessionAccess = HasPermission(currentUserPermissions, study.id, PermissionEnum.ViewFvcSessionsWidget);
    const hasSpirometrySessionBreakdownAccess = HasPermission(currentUserPermissions, study.id, PermissionEnum.ViewSessionsStatusBreakdownWidget);

    const noWidgets = reportWidgets.length === 0
        && !hasDocWidgetAccess && !hasProgressWidgetAccess && !hasCoughSessionBreakdownAccess && !hasSpirometrySessionAccess && !hasSpirometrySessionBreakdownAccess;

    if (noWidgets) {
        if (study.welcomeMessage) {
            return <WelcomeWidget />;
        }
        return <NoWidgets />;
    }

    return (<>
        <div className="row">
            <WelcomeWidget />
        </div>

        <div className="row">
            <ProgressWidgetApp />
            <DocumentsWidgetApp />
            <CoughSessionBreakdownWidget />
        </div>

        <div className="row">
            <SpirometrySessionsWidget />
            <SpirometrySessionBreakdownWidget />
        </div>
        {reportWidgets.length > 0 ? <ReportWidgets widgets={reportWidgets} /> : null}
    </>);
}
